import { Link, useLocation } from 'react-router-dom';
import SportIcon from '@/components/common/SportIcon';
import { getSportLabel } from '@/config/sports';
import { useGetMatchesGroupedByCompetitionQuery } from '@/store/api/sportsApi';
import { flattenFixtureGroups, sortFixturesByStartTime } from '@/utils';

/**
 * Header row of the matches live right now: one link per match (to its match page), each with
 * a blinking red live dot, shown just before the search box. Same request as `useInplayCounts`, so it shares that cache. Hidden when nothing
 * is live.
 */
const LiveMatchTicker = ({ className = '' }: { className?: string }) => {
  const { pathname } = useLocation();
  const { data } = useGetMatchesGroupedByCompetitionQuery(
    { sortBy: 'time', inplay: true },
    { pollingInterval: 30000 }
  );
  const matches = sortFixturesByStartTime(flattenFixtureGroups(data?.fixture ?? {}));

  if (matches.length === 0) return null;

  return (
    <div className={`flex min-w-0 items-center ${className}`}>
      <nav
        aria-label="Live matches"
        className="flex min-w-0 gap-2 overflow-x-auto [scrollbar-width:none]"
      >
        {matches.map((match) => {
          const to = `/game-details/${match.sport}/${match.id}`;
          const active = decodeURIComponent(pathname) === to;
          return (
            <Link
              key={match.id}
              to={to}
              title={`${match.name} · ${match.competition?.name ?? ''}`}
              className={`flex h-8 max-w-[240px] shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition ${
                active
                  ? 'border-gold bg-gold/10 text-gold'
                  : 'border-shell-border bg-shell-raised text-shell-fg hover:border-gold'
              }`}
            >
              <span
                aria-hidden="true"
                data-live-dot
                className="size-2 shrink-0 animate-live-blink rounded-full bg-danger-red motion-reduce:animate-none"
              />
              <SportIcon name={getSportLabel(match.sport) ?? match.sport} className="size-3.5" />
              <span className="truncate">{match.name}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default LiveMatchTicker;
